<template>
  <Teleport to="body">
    <!-- Background -->
    <Transition
      name="modal-fade"
      appear>
      <div
        v-if="stack.length > 0"
        class="js-host-modal fixed top-[var(--env-bar-height)] left-0 z-1000 h-[calc(100dvh-var(--env-bar-height))] w-full bg-black/35" />
    </Transition>

    <!-- Dialog -->
    <Transition
      name="modal-fade"
      appear>
      <div
        v-if="stack.length > 0"
        class="js-host-modal fixed top-[var(--env-bar-height)] left-0 z-1001 flex h-[calc(100dvh-var(--env-bar-height))] w-full items-start overflow-auto">
        <div
          v-for="(entry, index) in stack"
          :key="entry.id"
          v-show="index === stack.length - 1"
          ref="stackItemEl"
          role="dialog"
          aria-modal="true"
          :class="[
            'relative mx-auto my-[10vh] min-h-50 max-w-[90%] rounded-xl bg-surface shadow-sm',
            entry.width === 'sm' ? 'w-115' : '',
            entry.width === 'md' ? 'w-175' : '',
            entry.width === 'lg' ? 'w-200' : '',
            !entry.width ? 'w-175' : '',
          ]">
          <component
            v-if="registry[entry.name]"
            :is="registry[entry.name]"
            v-bind="entry.props"
            @close="closeEntry(entry.id)" />
          <div
            v-if="!entry.hideCloseButton"
            class="absolute top-0 right-0 h-12 w-12 cursor-pointer p-3 text-text-primary"
            @click="requestCloseTop">
            <IconLucideX />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useDismissablePriority } from '@team-milva/lab-components'

/**
 * Import all modal components that can be shown globally
 * (You can lazy-load these if you want)
 */
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import InviteModal from '@/components/modals/InviteModal.vue'
import NewLabModal from '@/components/modals/NewLabModal.vue'
import UnsavedChangesModal from '@/components/modals/UnsavedChangesModal.vue'
import {
  confirmLeaveUnsaved,
  isModalDirty,
} from '@/composables/useUnsavedChangesGuard'
import { focusFirstElement, trapTabFocus, wireDialogLabel } from '@/globals/typescript/utils/focusManagement'

export default {
  name: 'ModalHost',

  data() {
    return {
      previousActiveElement: null as HTMLElement | null,
      dismissable: useDismissablePriority(),
    }
  },

  computed: {
    ...mapState(useModalStore, ['top', 'stack']),
    /**
     * Modal registry:
     * key = modal name used by modal.open(name)
     * value = Vue component
     */
    registry(): Record<string, unknown> {
      return {
        ConfirmationModal,
        InviteModal,
        NewLabModal,
        UnsavedChangesModal,
      }
    },
  },
  watch: {
    'stack.length'(newLength: number, oldLength: number) {
      if (newLength > oldLength) {
        if (oldLength === 0) {
          this.previousActiveElement = document.activeElement as HTMLElement | null
          this.dismissable.activate()
        }
        this.$nextTick(this.onTopModalReady)
      } else if (newLength < oldLength) {
        if (newLength === 0) {
          this.previousActiveElement?.focus?.()
          this.previousActiveElement = null
          this.dismissable.deactivate()
        } else {
          this.$nextTick(this.onTopModalReady)
        }
      }
    },
  },
  methods: {
    ...mapActions(useModalStore, ['closeEntry']),
    // The two close paths ModalHost owns itself — Escape and the X button.
    // A modal's own `@close` is deliberately left unguarded: it fires after a
    // successful save too, where the form is still "dirty" relative to the
    // empty state it started from, and warning there would be nonsense.
    //
    // Everything closes by entry id, never by "pop the top": every modal stays
    // mounted beneath later entries, so by the time an answer arrives the top
    // of the stack may be a different layer than the one being closed.
    async requestCloseTop() {
      const topEntry = this.top
      if (!topEntry) {
        return
      }
      if (!isModalDirty(topEntry.id)) {
        this.closeEntry(topEntry.id)
        return
      }

      const shouldLeave = await confirmLeaveUnsaved({
        parentModalID: topEntry.id,
      })
      // The confirmation has already closed itself; closing the guarded modal
      // by id is a no-op if it closed on its own while the question was open.
      if (shouldLeave) {
        this.closeEntry(topEntry.id)
      }
    },
    getTopModalEl(): HTMLElement | null {
      const stackItemEls = this.$refs.stackItemEl as HTMLElement[] | undefined
      if (!stackItemEls?.length) {
        return null
      }
      return stackItemEls[stackItemEls.length - 1] ?? null
    },
    onTopModalReady() {
      focusFirstElement(this.getTopModalEl())
      wireDialogLabel(this.getTopModalEl())
    },
    onKeydown(e: KeyboardEvent) {
      if (!this.top) {
        return
      }

      if (e.key === 'Escape') {
        // Defers to a nested overlay (combobox dropdown, date picker, etc.)
        // that was opened after this modal — see useDismissablePriority.
        if (!this.dismissable.isTop()) {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        if (this.top?.useEasyClose) {
          void this.requestCloseTop()
        }
        return
      }

      if (e.key === 'Tab') {
        // Same deferral as Escape above: if something opened on top of this
        // modal currently holds focus, don't fight it — trapTabFocus would
        // otherwise see focus as having "escaped" this dialog's container
        // and force it back here on every Tab press.
        if (!this.dismissable.isTop()) {
          return
        }
        trapTabFocus(e, this.getTopModalEl())
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown, { passive: false })
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown as EventListener)
    this.dismissable.deactivate()
  },
}
</script>

<style scoped>
/* Fade */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 120ms ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>

